import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchAdminEntities, type AdminSearchResponse } from "../api/adminSearchApi";
import { useAdminSession } from "../context/AdminSessionContext";
import { hasRoleAtLeast } from "../utils/roleHelpers";

interface Props {
  placeholder?: string;
}

const EMPTY_RESULTS: AdminSearchResponse = {
  users: [],
  contents: [],
  parts: [],
  purchases: [],
  payouts: [],
};

export default function GlobalSearchBar({ placeholder = "Kullanici, seri, bolum, satin alim veya payout ara..." }: Props) {
  const navigate = useNavigate();
  const { session } = useAdminSession();
  const creatorRestricted = useMemo(() => {
    if (!session?.roles?.length) {
      return false;
    }
    return hasRoleAtLeast(session.roles, "creator") && !hasRoleAtLeast(session.roles, "moderator");
  }, [session]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AdminSearchResponse>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const response = await searchAdminEntities(query);
      setResults(response);
      setLoading(false);
      setOpen(true);
    }, 280);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const totalCount = useMemo(
    () => results.users.length + results.contents.length + results.parts.length + results.purchases.length + results.payouts.length,
    [results],
  );

  if (creatorRestricted) {
    return null;
  }

  const handleNavigate = (path: string) => {
    setOpen(false);
    setQuery("");
    setResults(EMPTY_RESULTS);
    navigate(path);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
              d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
        />
        {loading && <span className="text-[11px] font-semibold text-slate-500">Araniyor...</span>}
      </div>

      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200/90 bg-white/95 shadow-[0_18px_34px_rgba(13,32,58,0.18)] backdrop-blur">
          {totalCount === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">Eslesen sonuc bulunamadi.</div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto admin-scroll-area text-sm">
              {results.users.length > 0 && (
                <Section title="Kullanicilar">
                  {results.users.map((user) => (
                    <ResultRow
                      key={user.id}
                      title={user.name}
                      subtitle={user.email}
                      badge={user.roles?.[0]}
                      onSelect={() => handleNavigate(`/admin/users-360?tab=users&focus=${user.id}`)}
                    />
                  ))}
                </Section>
              )}

              {results.contents.length > 0 && (
                <Section title="Icerikler">
                  {results.contents.map((content) => (
                    <ResultRow
                      key={content.id}
                      title={content.title}
                      subtitle={content.author ?? ""}
                      badge={content.isApproved ? "Onayli" : content.submissionStatus ?? "Taslak"}
                      onSelect={() => handleNavigate(`/admin/content-360?tab=content&contentId=${content.id}`)}
                    />
                  ))}
                </Section>
              )}

              {results.parts.length > 0 && (
                <Section title="Bolumler">
                  {results.parts.map((part) => (
                    <ResultRow
                      key={part.id}
                      title={part.title}
                      subtitle={part.contentTitle ? `${part.contentTitle} - #${part.order ?? "-"}` : undefined}
                      badge={part.isApproved ? "Onayli" : "Bekliyor"}
                      onSelect={() => handleNavigate(`/admin/content-360?tab=content&contentId=${part.contentId ?? ""}`)}
                    />
                  ))}
                </Section>
              )}

              {results.purchases.length > 0 && (
                <Section title="Satin Alimlar">
                  {results.purchases.map((purchase) => (
                    <ResultRow
                      key={purchase.id}
                      title={purchase.title}
                      subtitle={purchase.subtitle ?? purchase.createdAtUtc}
                      badge={purchase.status}
                      onSelect={() => handleNavigate(`/admin/finance?tab=sales-ledger&focusPurchase=${purchase.id}`)}
                    />
                  ))}
                </Section>
              )}

              {results.payouts.length > 0 && (
                <Section title="Payout Kayitlari">
                  {results.payouts.map((payout) => (
                    <ResultRow
                      key={payout.id}
                      title={payout.title}
                      subtitle={payout.subtitle ?? payout.createdAtUtc}
                      badge={payout.status}
                      onSelect={() => handleNavigate(`/admin/finance?tab=payout-queue&focusPayout=${payout.id}`)}
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-slate-100 px-4 py-3 last:border-b-0">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{title}</p>
      <div className="divide-y divide-slate-100">{children}</div>
    </section>
  );
}

function ResultRow({
  title,
  subtitle,
  badge,
  onSelect,
}: {
  title: string;
  subtitle?: string;
  badge?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center justify-between gap-3 px-1 py-2 text-left transition hover:bg-slate-50"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-slate-800">{title}</div>
        {subtitle && <div className="truncate text-xs text-slate-500">{subtitle}</div>}
      </div>

      {badge && (
        <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
          {badge}
        </span>
      )}
    </button>
  );
}
